import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Box,
  Card,
  CardContent,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import HeaderResponsive from "../../../components/layouts/HeaderResponsive";
import Title from "../../../components/Title";
import StickyListToolbar, {
  stickyListRegionWidth,
} from "../../../components/layouts/StickyListToolbar";
import { useDrawer } from "../../../components/context/DrawerContext";
import PermissionButton from "../../../components/common/PermissionButton";
import { useResourcePermissions } from "../../../hooks/useResourcePermissions";
import SimpleCustomTable from "../../../components/table/SimpleCustomTable";
import { SimpleColumn, SimpleRow, RowId } from "../../../components/table/interface";

interface ShowAlerts {
  showAlert: (message: string, severity?: "success" | "info" | "warning" | "error") => void;
  showLoading: () => void;
  hideLoading: () => void;
}

type CollectRow = {
  id: number;
  name: string;
  office_id: number | null;
  office_name: string;
  alert_active: boolean;
  inhabilitar_cobro: boolean;
};

const MENU_ROUTE = "/procesos/aviso-licencia-mobile";

const API_NOT_FOUND_HINT =
  "El servidor respondió 404 para este módulo. Comprueba la URL de la API en la marca y que el backend esté actualizado.";

const MobileLicenseNoticeAdmin: React.FC<ShowAlerts> = ({ showAlert, showLoading, hideLoading }) => {
  document.title = "Aviso licencia app móvil";
  const { drawerOpen } = useDrawer();
  const { canRead, canUpdate } = useResourcePermissions(MENU_ROUTE);

  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [durationSeconds, setDurationSeconds] = useState(5);
  const [collects, setCollects] = useState<CollectRow[]>([]);
  const [notAvailable, setNotAvailable] = useState(false);

  const load = useCallback(async () => {
    showLoading();
    try {
      const { data } = await axios.get("procesos/mobile-license-notice");
      const tpl = data?.data?.template;
      const rows: CollectRow[] = data?.data?.collects || [];
      setTitle(String(tpl?.title ?? ""));
      setBody(String(tpl?.body ?? ""));
      setDurationSeconds(Number(tpl?.duration_seconds) || 5);
      setCollects(rows);
      setNotAvailable(false);
    } catch (error: unknown) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 404) {
        setNotAvailable(true);
        showAlert(API_NOT_FOUND_HINT, "info");
      } else {
        showAlert("No se pudo cargar la configuración.", "error");
      }
    } finally {
      hideLoading();
    }
  }, [hideLoading, showAlert, showLoading]);

  useEffect(() => {
    void load();
  }, [load]);

  const setCollectActive = useCallback((id: number, next: boolean) => {
    setCollects((prev) =>
      prev.map((row) => (row.id === id ? { ...row, alert_active: next } : row)),
    );
  }, []);

  const setCollectDisabled = useCallback((id: number, next: boolean) => {
    setCollects((prev) =>
      prev.map((row) => (row.id === id ? { ...row, inhabilitar_cobro: next } : row)),
    );
  }, []);

  const save = async () => {
    if (!canUpdate) {
      showAlert("Sin permiso para guardar.", "warning");
      return;
    }
    showLoading();
    try {
      await axios.put("procesos/mobile-license-notice", {
        template: {
          title,
          body,
          duration_seconds: durationSeconds,
        },
        collects: collects.map((c) => ({
          id: c.id,
          alert_active: c.alert_active,
          inhabilitar_cobro: c.inhabilitar_cobro,
        })),
      });
      showAlert("Configuración guardada.", "success");
      await load();
    } catch (error: unknown) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 404) {
        showAlert(API_NOT_FOUND_HINT, "warning");
      } else {
        showAlert("Error al guardar.", "error");
      }
    } finally {
      hideLoading();
    }
  };

  const tableRows: SimpleRow[] = useMemo(
    () =>
      collects.map((c) => ({
        id: c.id as RowId,
        name: c.name,
        office_name: c.office_name,
        alert_active: c.alert_active,
        inhabilitar_cobro: c.inhabilitar_cobro,
      })),
    [collects],
  );

  const columns: SimpleColumn[] = useMemo(
    () => [
      { field: "name", headerName: "Cobro", minWidth: 160 },
      { field: "office_name", headerName: "Oficina", minWidth: 140 },
      {
        field: "alert_active",
        headerName: "Aviso activo en app",
        minWidth: 160,
        renderCell: ({ row }) => (
          <Switch
            checked={Boolean(row.alert_active)}
            disabled={!canUpdate}
            onChange={(_, checked) => setCollectActive(Number(row.id), checked)}
            size="small"
          />
        ),
      },
      {
        field: "inhabilitar_cobro",
        headerName: "Inhabilitar cobro",
        minWidth: 150,
        renderCell: ({ row }) => (
          <Switch
            checked={Boolean(row.inhabilitar_cobro)}
            disabled={!canUpdate}
            onChange={(_, checked) => setCollectDisabled(Number(row.id), checked)}
            size="small"
          />
        ),
      },
    ],
    [canUpdate, setCollectActive, setCollectDisabled],
  );

  return (
    <HeaderResponsive>
      <StickyListToolbar>
        <Title>Aviso licencia app móvil</Title>
        <Box sx={{ ml: "auto" }}>
          <PermissionButton
            allowed={canUpdate}
            deniedTooltip="Sin permiso para guardar"
            size="small"
            variant="contained"
            onClick={() => void save()}
          >
            Guardar
          </PermissionButton>
        </Box>
      </StickyListToolbar>

      <Card>
        <CardContent
          sx={{
            width: (theme) => stickyListRegionWidth(theme, drawerOpen),
            maxWidth: "100%",
            boxSizing: "border-box",
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Mensaje global mostrado en la app móvil al iniciar sesión (unos segundos). Active el aviso solo en los
            cobros que deban verlo; desactívelo cuando estén al día.
          </Typography>

          {!canRead ? (
            <Typography color="error">Sin permiso para consultar esta pantalla.</Typography>
          ) : notAvailable ? (
            <Typography>No disponible en esta API.</Typography>
          ) : (
            <>
              <TextField
                label="Título"
                fullWidth
                margin="normal"
                value={title}
                disabled={!canUpdate}
                onChange={(e) => setTitle(e.target.value)}
              />
              <TextField
                label="Mensaje"
                fullWidth
                margin="normal"
                multiline
                minRows={4}
                value={body}
                disabled={!canUpdate}
                onChange={(e) => setBody(e.target.value)}
              />
              <TextField
                label="Segundos visibles (1–120)"
                type="number"
                margin="normal"
                sx={{ maxWidth: 220 }}
                value={durationSeconds}
                disabled={!canUpdate}
                onChange={(e) => setDurationSeconds(Math.max(1, Math.min(120, Number(e.target.value) || 5)))}
              />
              <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
                Cobros
              </Typography>
              {collects.length === 0 ? (
                <Typography variant="body2">No hay cobros visibles para su usuario.</Typography>
              ) : (
                <SimpleCustomTable
                  rows={tableRows}
                  columns={columns}
                  maxHeight={480}
                  rowHeight={36}
                  showBorders
                  showCount
                />
              )}
            </>
          )}
        </CardContent>
      </Card>
    </HeaderResponsive>
  );
};

export default MobileLicenseNoticeAdmin;
